import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Alert, AlertIcon, Button, Heading } from '@chakra-ui/react';
import { FaUserPlus } from 'react-icons/fa';
import UserContext from '../../../context/userContext';
import { Loading } from '../../Common/Loading';
import TablaSociosAdmin from './TablaSociosAdmin/TablaSociosAdmin';
import obtenerTitulares from '../../../services/obtenerTitulares';
import { filtrarTitulares } from '../../../services/filtrarTitulares';
import { filtrarCabana } from '../../../services/filtrarCabana';
import '../../../styles/socios.scss';
import { useTranslation } from 'react-i18next';

export default function MostrarSociosAdmin() {
  const { user, isLoadingContext, setIsLoadingContext } =
    useContext(UserContext);

  const { t } = useTranslation();
  const [socios, setSocios] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState({ error: false, msg: '' });
  const navigate = useNavigate();
  const [selectedCabana, setSelectedCabana] = useState('TODAS');
  // titular = true, cotitular = false
  const [selectedTipoUsuario, setSelectedTipoUsuario] = useState(null);
  const [activeTipoUsuario, setActiveTipoUsuario] = useState('TODOS');
  const [activeCabana, setActiveCabana] = useState('TODAS');

  useEffect(() => {
    if (search.length === 0) {
      setIsLoadingContext(true);
      handleTitulares(user.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, user.id]);

  const handleTitulares = (id) => {
    setIsLoadingContext(true);
    obtenerTitulares(id)
      .then((res) => {
        if (res.status === 200) {
          setSocios(res.data.titulares);
        } else {
          setError({ error: true, msg: 'Error obteniendo titulares' });
        }
      })
      .catch((err) => {
        setError({ error: true, msg: 'Error obteniendo usuarios.' });
      })
      .finally(() => setIsLoadingContext(false));
  };

  const handleSearchTerm = (e) => {
    setSearch(e.target.value);
    setError({ error: false, msg: '' });
  };

  const containsSpecialCharacters = (text) => {
    const regex = /^[^{}[\]#&%!¡"()='?;:,.¿+~¨´`|¬°*$\\^/]*$/;
    return !regex.test(text);
  };

  const containsSpaces = (text) => {
    return text.includes(' ');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim() === '') {
      setError({
        error: true,
        msg: t('NO_SPACES_ALLOWED')
      });
      return;
    } else if (containsSpecialCharacters(search)) {
      setError({ error: true, msg: 'No se permiten caracteres especiales.' });
      return;
    } else if (containsSpaces(search)) {
      setError({ error: true, msg: 'No se permiten espacios en blanco.' });
      return;
    }

    setIsLoadingContext(true);
    filtrarTitulares(user.id, search)
      .then((res) => {
        if (res.status === 200) {
          setSocios(res.data.users);
          setError({ error: false, msg: '' });
        } else {
          setError({ error: true, msg: 'Error filtrando usuarios' });
        }
        setIsLoadingContext(false);
      })
      .catch((err) =>
        setError({ error: true, msg: 'Error obteniendo usuarios.' })
      );
  };

  const handleFiltrarCabana = (cabana) => {
    setSelectedCabana(cabana);
    setActiveCabana(cabana);
  };

  const handleFiltrarTipoUsuario = (tipo) => {
    setSelectedTipoUsuario(tipo === 'TODOS' ? null : tipo);
    setActiveTipoUsuario(tipo);
  };

  const filteredSocios = socios.filter((socio) => {
    const matchesCabana =
      selectedCabana === 'TODAS' || socio.tipoCabana === selectedCabana;
    const matchesTipoUsuario =
      selectedTipoUsuario === null || socio.coTitular !== selectedTipoUsuario;
    return matchesCabana && matchesTipoUsuario;
  });

  return (
    <div className="p-5">
      <div className="d-flex justify-content-between">
        <div>
          <Heading
            as="h3"
            size="lg"
          >
            {t('FILTER_SOCIO_ADMIN')}
          </Heading>
          <p className="my-2">
            {t('ENTER_USER_INFO')}
          </p>

          <form className="d-flex justify-content-between my-3">
            <input
              className="form-control me-3"
              type="text"
              onChange={handleSearchTerm}
            />
            <Button
              colorScheme={'blue'}
              type="submit"
              onClick={handleSearch}
            >
              {t('SEARCH')}
            </Button>
          </form>
          {error.error && (
            <Alert status="error">
              <AlertIcon />
              {
                typeof error.msg === 'string' ? error.msg : error.msg.hasOwnProperty('message') ? error.msg.message : 'Error'
              }
            </Alert>
          )}
        </div>
        <div>
          <Heading
            as="h3"
            size="lg"
          >
            {t('CABIN_TYPE')}
          </Heading>
          <div className="d-flex my-5">
            <Button
              mx={2}
              colorScheme={activeCabana === 'MEZQUITE' ? 'orange' : 'gray'}
              opacity={activeCabana === 'MEZQUITE' ? 1 : 0.5}
              onClick={() => handleFiltrarCabana('MEZQUITE')}
            >
              {t('MEZQUITE')}
            </Button>
            <Button
              mx={2}
              colorScheme={activeCabana === 'PIRUL' ? 'orange' : 'gray'}
              opacity={activeCabana === 'PIRUL' ? 1 : 0.5}
              onClick={() => handleFiltrarCabana('PIRUL')}
            >
              {t('PIRUL')}
            </Button>
            <Button
              mx={2}
              colorScheme={activeCabana === 'BAMBU' ? 'orange' : 'gray'}
              opacity={activeCabana === 'BAMBU' ? 1 : 0.5}
              onClick={() => handleFiltrarCabana('BAMBU')}
            >
              {t('BAMBU')}
            </Button>
            <Button
              mx={2}
              colorScheme={activeCabana === 'TODAS' ? 'orange' : 'gray'}
              opacity={activeCabana === 'TODAS' ? 1 : 0.5}
              onClick={() => handleFiltrarCabana('TODAS')}
            >
              {t('ALL')}
            </Button>
          </div>
        </div>

        <div>
          <Heading
            as="h3"
            size="lg"
          >
            {t('USER_TYPE')}
          </Heading>
          <div className="d-flex my-5">
            <Button
              mx={2}
              colorScheme={activeTipoUsuario === true ? 'orange' : 'gray'}
              opacity={activeTipoUsuario === true ? 1 : 0.5}
              onClick={() => handleFiltrarTipoUsuario(true)}
            >
              {t('HOLDER')}
            </Button>
            <Button
              mx={2}
              colorScheme={activeTipoUsuario === false ? 'orange' : 'gray'}
              opacity={activeTipoUsuario === false ? 1 : 0.5}
              onClick={() => handleFiltrarTipoUsuario(false)}
            >
              {t('CO_HOLDER')}
            </Button>
            <Button
              mx={2}
              colorScheme={activeTipoUsuario === 'TODOS' ? 'orange' : 'gray'}
              opacity={activeTipoUsuario === 'TODOS' ? 1 : 0.5}
              onClick={() => handleFiltrarTipoUsuario('TODOS')}
            >
              {t('ALL_USERS')}
            </Button>
          </div>
        </div>
        <div>
          <button
            onClick={() => navigate('/crear-usuario')}
            className="btn btn-outline-orange circle-button"
          >
            <FaUserPlus style={{ width: "100%", height: "100%" }}/>
          </button>
        </div>
      </div>
      <div className="w-100 mt-5">
        {isLoadingContext ? (
          <Loading />
        ) : (
          <TablaSociosAdmin
            socios={filteredSocios}
            admin={user.id}
            setSocios={setSocios}
            rol={user.rol}
          />
        )}
      </div>
    </div>
  );
}
